import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { useClientProfile } from '../context/ClientProfileContext';
import { useAmazonRegistry } from '../context/AmazonBrandRegistryContext';
import VendrLayout from '../VendrLayout';
import FormSection from '../components/FormSection';
import FormField from '../components/FormField';
import { getSignedUrl } from '../utils/vendrStorage';
import './AmazonBrandRegistryForm.css';

const BUCKET = 'client-portal-uploads';

const TRADEMARK_OFFICES = [
  { value: 'USPTO', label: 'USPTO (United States)' },
  { value: 'UKIPO', label: 'UKIPO (United Kingdom)' },
  { value: 'EUIPO', label: 'EUIPO (European Union)' },
  { value: 'CIPO', label: 'CIPO (Canada)' },
  { value: 'IP Australia', label: 'IP Australia' },
  { value: 'IPONZ', label: 'IPONZ (New Zealand)' },
  { value: 'Other', label: 'Other' },
];

const emptyForm = {
  brand_trademark_name: '',
  trademark_office: '',
  trademark_registration_number: '',
  brand_category: '',
  brand_website_url: '',
  registered_business_address: '',
  ein_or_vat_number: '',
  trademark_contact_name: '',
  trademark_contact_email: '',
  trademark_contact_phone: '',
  sells_to_distributors: null,
  distribution_locations: '',
  licenses_trademarks: null,
  trademark_licensee_description: '',
};

async function uploadAmazonFile(file, clientProfileId, fieldName) {
  const ext = file.name.split('.').pop();
  const path = `${clientProfileId}/amazon-registry/${fieldName}.${ext}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: true });
  if (error) throw new Error(error.message);
  return path;
}

export default function AmazonBrandRegistryForm() {
  const navigate = useNavigate();
  const { profile, loading: profileLoading, isSetupComplete } = useClientProfile();
  const { registry, loading: registryLoading, isAmazonEnabled, isAmazonSubmitted, saveRegistry } = useAmazonRegistry();

  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState({
    brand_logo: null,
    product_image_1: null,
    product_image_2: null,
    product_image_3: null,
    product_image_4: null,
    product_image_5: null,
    manufacturer_document: null,
    supply_invoice: null,
  });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [savedPaths, setSavedPaths] = useState({});

  useEffect(() => {
    if (!registry) return;
    setForm({
      brand_trademark_name: registry.brand_trademark_name || '',
      trademark_office: registry.trademark_office || '',
      trademark_registration_number: registry.trademark_registration_number || '',
      brand_category: registry.brand_category || '',
      brand_website_url: registry.brand_website_url || '',
      registered_business_address: registry.registered_business_address || '',
      ein_or_vat_number: registry.ein_or_vat_number || '',
      trademark_contact_name: registry.trademark_contact_name || '',
      trademark_contact_email: registry.trademark_contact_email || '',
      trademark_contact_phone: registry.trademark_contact_phone || '',
      sells_to_distributors: registry.sells_to_distributors,
      distribution_locations: registry.distribution_locations || '',
      licenses_trademarks: registry.licenses_trademarks,
      trademark_licensee_description: registry.trademark_licensee_description || '',
    });
    setSavedPaths({
      brand_logo_path: registry.brand_logo_path,
      product_image_1_path: registry.product_image_1_path,
      product_image_2_path: registry.product_image_2_path,
      product_image_3_path: registry.product_image_3_path,
      product_image_4_path: registry.product_image_4_path,
      product_image_5_path: registry.product_image_5_path,
      manufacturer_document_path: registry.manufacturer_document_path,
      supply_invoice_path: registry.supply_invoice_path,
    });
  }, [registry]);

  if (profileLoading || registryLoading) {
    return (
      <VendrLayout>
        <div className="amazon-form-loading">Loading...</div>
      </VendrLayout>
    );
  }

  if (!isSetupComplete) return <Navigate to="/vendr/setup" replace />;
  if (!isAmazonEnabled) return <Navigate to="/vendr" replace />;

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const setFile = (field) => (e) => setFiles((prev) => ({ ...prev, [field]: e.target.files[0] || null }));
  const setRadio = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const uploadAllFiles = async () => {
    const paths = { ...savedPaths };
    const uploads = [
      ['brand_logo', 'brand_logo_path', 'brand-logo'],
      ['product_image_1', 'product_image_1_path', 'product-image-1'],
      ['product_image_2', 'product_image_2_path', 'product-image-2'],
      ['product_image_3', 'product_image_3_path', 'product-image-3'],
      ['product_image_4', 'product_image_4_path', 'product-image-4'],
      ['product_image_5', 'product_image_5_path', 'product-image-5'],
      ['manufacturer_document', 'manufacturer_document_path', 'manufacturer-doc'],
      ['supply_invoice', 'supply_invoice_path', 'supply-invoice'],
    ];
    for (const [fileKey, pathKey, fieldName] of uploads) {
      if (files[fileKey]) {
        paths[pathKey] = await uploadAmazonFile(files[fileKey], profile.id, fieldName);
      }
    }
    return paths;
  };

  const validate = (paths) => {
    if (!form.brand_trademark_name.trim()) return 'Brand Trademark Name is required.';
    if (!paths.brand_logo_path) return 'Brand Logo is required.';
    if (!form.trademark_office) return 'Trademark Office is required.';
    if (!form.trademark_registration_number.trim()) return 'Trademark Registration Number is required.';
    if (!form.brand_category.trim()) return 'Brand Category is required.';
    if (!form.brand_website_url.trim()) return 'Brand Website URL is required.';
    if (!paths.product_image_1_path) return 'At least one product image is required.';
    if (!paths.manufacturer_document_path) return 'Manufacturer Document is required.';
    if (!paths.supply_invoice_path) return 'Supply Invoice is required.';
    if (!form.registered_business_address.trim()) return 'Registered Business Address is required.';
    if (!form.ein_or_vat_number.trim()) return 'EIN / VAT Number is required.';
    if (!form.trademark_contact_name.trim()) return 'Trademark Contact Name is required.';
    if (!form.trademark_contact_email.trim()) return 'Trademark Contact Email is required.';
    if (!form.trademark_contact_phone.trim()) return 'Trademark Contact Phone is required.';
    if (form.sells_to_distributors === null) return 'Please answer: Does your brand sell to distributors?';
    if (form.licenses_trademarks === null) return 'Please answer: Does your brand license trademarks?';
    return null;
  };

  const handleSaveDraft = async () => {
    setError('');
    setSaving(true);
    try {
      const paths = await uploadAllFiles();
      const result = await saveRegistry({ ...form, ...paths }, false);
      if (!result.success) setError(result.error || 'Failed to save draft.');
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const paths = await uploadAllFiles();
      const validationError = validate(paths);
      if (validationError) {
        setError(validationError);
        setSubmitting(false);
        return;
      }
      const result = await saveRegistry({ ...form, ...paths }, true);
      if (!result.success) {
        setError(result.error || 'Failed to submit.');
      } else {
        navigate('/vendr');
      }
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isAmazonSubmitted) {
    return <SubmittedView registry={registry} />;
  }

  return (
    <VendrLayout>
      <div className="amazon-form">
        <div className="amazon-form-header">
          <h1>Amazon Brand Registry</h1>
          <p>
            Please complete all sections below. Your information will be used by MarketVendr to
            register your brand on Amazon.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <FormSection title="Brand Information">
            <FormField
              label="Brand Trademark Name"
              name="brand_trademark_name"
              value={form.brand_trademark_name}
              onChange={set('brand_trademark_name')}
              required
            />
            <FormField
              label="Brand Logo"
              name="brand_logo"
              inputType="file"
              accept="image/*"
              onChange={setFile('brand_logo')}
              fileSaved={savedPaths.brand_logo_path ? savedPaths.brand_logo_path.split('/').pop() : false}
              required
            />
            <FormField
              label="Trademark Office"
              name="trademark_office"
              inputType="select"
              value={form.trademark_office}
              onChange={set('trademark_office')}
              selectOptions={TRADEMARK_OFFICES}
              required
            />
            <FormField
              label="Trademark Registration or Serial Number"
              name="trademark_registration_number"
              value={form.trademark_registration_number}
              onChange={set('trademark_registration_number')}
              required
            />
            <FormField
              label="Category That Best Describes Your Brand"
              name="brand_category"
              value={form.brand_category}
              onChange={set('brand_category')}
              placeholder="e.g. apparel, beauty, electronics"
              required
            />
            <FormField
              label="Official Brand Website URL"
              name="brand_website_url"
              inputType="url"
              value={form.brand_website_url}
              onChange={set('brand_website_url')}
              placeholder="https://example.com"
              required
            />
          </FormSection>

          <FormSection title="Product Information">
            <FormField
              label="Product Image 1"
              name="product_image_1"
              inputType="file"
              accept="image/*"
              onChange={setFile('product_image_1')}
              fileSaved={savedPaths.product_image_1_path ? savedPaths.product_image_1_path.split('/').pop() : false}
              required
              helperText="Include clear front and back images. Upload up to 5 images."
            />
            <FormField
              label="Product Image 2"
              name="product_image_2"
              inputType="file"
              accept="image/*"
              onChange={setFile('product_image_2')}
              fileSaved={savedPaths.product_image_2_path ? savedPaths.product_image_2_path.split('/').pop() : false}
              optional
            />
            <FormField
              label="Product Image 3"
              name="product_image_3"
              inputType="file"
              accept="image/*"
              onChange={setFile('product_image_3')}
              fileSaved={savedPaths.product_image_3_path ? savedPaths.product_image_3_path.split('/').pop() : false}
              optional
            />
            <FormField
              label="Product Image 4"
              name="product_image_4"
              inputType="file"
              accept="image/*"
              onChange={setFile('product_image_4')}
              fileSaved={savedPaths.product_image_4_path ? savedPaths.product_image_4_path.split('/').pop() : false}
              optional
            />
            <FormField
              label="Product Image 5"
              name="product_image_5"
              inputType="file"
              accept="image/*"
              onChange={setFile('product_image_5')}
              fileSaved={savedPaths.product_image_5_path ? savedPaths.product_image_5_path.split('/').pop() : false}
              optional
            />
            <FormField
              label="Manufacturer Document"
              name="manufacturer_document"
              inputType="file"
              accept="image/*,application/pdf"
              onChange={setFile('manufacturer_document')}
              fileSaved={savedPaths.manufacturer_document_path ? savedPaths.manufacturer_document_path.split('/').pop() : false}
              required
              helperText="Document qualifying you as a manufacturer or arrangement with a third-party manufacturer."
            />
            <FormField
              label="Sourcing / Manufacturing / Supply Invoice"
              name="supply_invoice"
              inputType="file"
              accept="image/*,application/pdf"
              onChange={setFile('supply_invoice')}
              fileSaved={savedPaths.supply_invoice_path ? savedPaths.supply_invoice_path.split('/').pop() : false}
              required
              helperText="A recent invoice confirming your supply chain."
            />
          </FormSection>

          <FormSection title="Business Information">
            <FormField
              label="Registered Business Address"
              name="registered_business_address"
              inputType="textarea"
              value={form.registered_business_address}
              onChange={set('registered_business_address')}
              rows={3}
              fullWidth
              required
            />
            <FormField
              label="EIN / VAT Number or Equivalent"
              name="ein_or_vat_number"
              value={form.ein_or_vat_number}
              onChange={set('ein_or_vat_number')}
              required
            />
            <FormField
              label="Trademark Contact Name"
              name="trademark_contact_name"
              value={form.trademark_contact_name}
              onChange={set('trademark_contact_name')}
              helperText="Name of the person listed on the trademark registration."
              required
            />
            <FormField
              label="Trademark Contact Email"
              name="trademark_contact_email"
              inputType="email"
              value={form.trademark_contact_email}
              onChange={set('trademark_contact_email')}
              required
            />
            <FormField
              label="Trademark Contact Phone"
              name="trademark_contact_phone"
              inputType="tel"
              value={form.trademark_contact_phone}
              onChange={set('trademark_contact_phone')}
              required
            />
          </FormSection>

          <FormSection title="Distribution & Licensing">
            <div className="amazon-form-radio-group">
              <p className="amazon-form-radio-label">
                Does your brand sell to distributors? <span className="amazon-form-required">*</span>
              </p>
              <label className="amazon-form-radio-option">
                <input
                  type="radio"
                  name="sells_to_distributors"
                  checked={form.sells_to_distributors === true}
                  onChange={() => setRadio('sells_to_distributors', true)}
                />
                Yes
              </label>
              <label className="amazon-form-radio-option">
                <input
                  type="radio"
                  name="sells_to_distributors"
                  checked={form.sells_to_distributors === false}
                  onChange={() => setRadio('sells_to_distributors', false)}
                />
                No
              </label>
            </div>

            {form.sells_to_distributors === true && (
              <FormField
                label="Where are your products distributed?"
                name="distribution_locations"
                inputType="textarea"
                value={form.distribution_locations}
                onChange={set('distribution_locations')}
                rows={3}
                fullWidth
              />
            )}

            <div className="amazon-form-radio-group">
              <p className="amazon-form-radio-label">
                Does your brand license trademarks to others? <span className="amazon-form-required">*</span>
              </p>
              <label className="amazon-form-radio-option">
                <input
                  type="radio"
                  name="licenses_trademarks"
                  checked={form.licenses_trademarks === true}
                  onChange={() => setRadio('licenses_trademarks', true)}
                />
                Yes
              </label>
              <label className="amazon-form-radio-option">
                <input
                  type="radio"
                  name="licenses_trademarks"
                  checked={form.licenses_trademarks === false}
                  onChange={() => setRadio('licenses_trademarks', false)}
                />
                No
              </label>
            </div>

            {form.licenses_trademarks === true && (
              <FormField
                label="Describe who manufactures or sells products under your intellectual property"
                name="trademark_licensee_description"
                inputType="textarea"
                value={form.trademark_licensee_description}
                onChange={set('trademark_licensee_description')}
                rows={4}
                fullWidth
              />
            )}
          </FormSection>

          {error && <p className="amazon-form-error">{error}</p>}

          <div className="amazon-form-actions">
            <button
              type="button"
              className="amazon-form-btn-draft"
              onClick={handleSaveDraft}
              disabled={saving || submitting}
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              type="submit"
              className="amazon-form-btn-submit"
              disabled={saving || submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </VendrLayout>
  );
}

function SubmittedView({ registry }) {
  const [fileUrls, setFileUrls] = useState({});

  useEffect(() => {
    const filePaths = {
      brand_logo_path: registry.brand_logo_path,
      product_image_1_path: registry.product_image_1_path,
      product_image_2_path: registry.product_image_2_path,
      product_image_3_path: registry.product_image_3_path,
      product_image_4_path: registry.product_image_4_path,
      product_image_5_path: registry.product_image_5_path,
      manufacturer_document_path: registry.manufacturer_document_path,
      supply_invoice_path: registry.supply_invoice_path,
    };
    const loadUrls = async () => {
      const urls = {};
      for (const [key, path] of Object.entries(filePaths)) {
        if (path) {
          const result = await getSignedUrl(path);
          if (result.success) urls[key] = result.url;
        }
      }
      setFileUrls(urls);
    };
    loadUrls();
  }, [registry]);

  const FileLink = ({ pathKey, label }) =>
    fileUrls[pathKey] ? (
      <a href={fileUrls[pathKey]} target="_blank" rel="noopener noreferrer" className="amazon-submitted-file-link">
        View {label}
      </a>
    ) : null;

  return (
    <VendrLayout>
      <div className="amazon-form amazon-form-submitted-view">
        <div className="amazon-form-submitted-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Form submitted — your MarketVendr representative will be in touch.
        </div>

        <div className="amazon-form-header">
          <h1>Amazon Brand Registry</h1>
        </div>

        <div className="amazon-submitted-section">
          <h2>Brand Information</h2>
          <div className="amazon-submitted-grid">
            <ReadField label="Brand Trademark Name" value={registry.brand_trademark_name} />
            <ReadField label="Trademark Office" value={registry.trademark_office} />
            <ReadField label="Registration Number" value={registry.trademark_registration_number} />
            <ReadField label="Brand Category" value={registry.brand_category} />
            <ReadField label="Website" value={registry.brand_website_url} />
            <div className="amazon-submitted-field">
              <span className="amazon-submitted-label">Brand Logo</span>
              <FileLink pathKey="brand_logo_path" label="Brand Logo" />
            </div>
          </div>
        </div>

        <div className="amazon-submitted-section">
          <h2>Product Information</h2>
          <div className="amazon-submitted-grid">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="amazon-submitted-field">
                <span className="amazon-submitted-label">Product Image {n}</span>
                <FileLink pathKey={`product_image_${n}_path`} label={`Product Image ${n}`} />
              </div>
            ))}
            <div className="amazon-submitted-field">
              <span className="amazon-submitted-label">Manufacturer Document</span>
              <FileLink pathKey="manufacturer_document_path" label="Manufacturer Document" />
            </div>
            <div className="amazon-submitted-field">
              <span className="amazon-submitted-label">Supply Invoice</span>
              <FileLink pathKey="supply_invoice_path" label="Supply Invoice" />
            </div>
          </div>
        </div>

        <div className="amazon-submitted-section">
          <h2>Business Information</h2>
          <div className="amazon-submitted-grid">
            <ReadField label="Registered Business Address" value={registry.registered_business_address} fullWidth />
            <ReadField label="EIN / VAT Number" value={registry.ein_or_vat_number} />
            <ReadField label="Trademark Contact Name" value={registry.trademark_contact_name} />
            <ReadField label="Trademark Contact Email" value={registry.trademark_contact_email} />
            <ReadField label="Trademark Contact Phone" value={registry.trademark_contact_phone} />
          </div>
        </div>

        <div className="amazon-submitted-section">
          <h2>Distribution &amp; Licensing</h2>
          <div className="amazon-submitted-grid">
            <ReadField label="Sells to Distributors" value={registry.sells_to_distributors ? 'Yes' : 'No'} />
            {registry.sells_to_distributors && (
              <ReadField label="Distribution Locations" value={registry.distribution_locations} fullWidth />
            )}
            <ReadField label="Licenses Trademarks" value={registry.licenses_trademarks ? 'Yes' : 'No'} />
            {registry.licenses_trademarks && (
              <ReadField label="Licensee Description" value={registry.trademark_licensee_description} fullWidth />
            )}
          </div>
        </div>
      </div>
    </VendrLayout>
  );
}

function ReadField({ label, value, fullWidth = false }) {
  return (
    <div className={`amazon-submitted-field${fullWidth ? ' amazon-submitted-field-full' : ''}`}>
      <span className="amazon-submitted-label">{label}</span>
      <span className="amazon-submitted-value">{value || '—'}</span>
    </div>
  );
}
